import { useState, useEffect } from 'react';
import api from '../services/api';
import {
  LuPlus, LuTrash2, LuPencil, LuCheck, LuX,
} from 'react-icons/lu';

const SLOTS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

export default function IndicatorsPage() {
  const [departments,  setDepartments]  = useState([]);
  const [selectedDept, setSelectedDept] = useState('');
  const [configs,      setConfigs]      = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [editingSlot,  setEditingSlot]  = useState(null);
  const [form,         setForm]         = useState({ name: '', weight: 10, active: true });
  const [saving,       setSaving]       = useState(false);
  const [error,        setError]        = useState('');
  const [success,      setSuccess]      = useState('');

  // Carrega departamentos uma vez
  useEffect(() => {
    api.get('/departments')
      .then((r) => setDepartments(r.data))
      .catch(console.error);
  }, []);

  // Carrega configs quando departamento muda
  useEffect(() => {
    if (!selectedDept) { setConfigs([]); return; }

    let cancelled = false;
    setLoading(true);
    setConfigs([]);
    setEditingSlot(null);
    setError('');

    api.get(`/departments/${selectedDept}/indicator-configs`)
      .then((r) => { if (!cancelled) setConfigs(r.data); })
      .catch((e) => { if (!cancelled) setError('Erro ao carregar indicadores: ' + (e.message || '')); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [selectedDept]);

  const totalWeight = configs.filter((c) => c.active).reduce((s, c) => s + (c.weight || 0), 0);

  const startEdit = (slot, cfg) => {
    setForm(cfg
      ? { name: cfg.name, weight: cfg.weight, active: cfg.active }
      : { name: '', weight: 10, active: true }
    );
    setEditingSlot(slot);
    setError('');
    setSuccess('');
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Nome é obrigatório'); return; }
    setSaving(true);
    setError('');
    try {
      const { data: saved } = await api.put(
        `/departments/${selectedDept}/indicator-configs/${editingSlot}`,
        { name: form.name.trim(), weight: Number(form.weight), active: form.active }
      );
      setConfigs((prev) => {
        const exists = prev.some((c) => c.slot === saved.slot);
        if (exists) return prev.map((c) => (c.slot === saved.slot ? saved : c));
        return [...prev, saved].sort((a, b) => a.slot - b.slot);
      });
      setEditingSlot(null);
      setSuccess(`"${saved.name}" salvo com sucesso`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.response?.data?.error || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (slot) => {
    if (!window.confirm(`Remover o indicador ${slot} deste departamento?`)) return;
    setError('');
    try {
      await api.delete(`/departments/${selectedDept}/indicator-configs/${slot}`);
      setConfigs((prev) => prev.filter((c) => c.slot !== slot));
    } catch (e) {
      setError(e.response?.data?.error || 'Erro ao remover');
    }
  };

  return (
    <div className="page-card">
      <div className="page-card-header">
        <div>
          <div className="page-card-title">Modelos de Avaliação</div>
          <div className="page-card-subtitle">Configure os indicadores e pesos por departamento</div>
        </div>
      </div>

      {/* Seleção de departamento */}
      <div style={{ marginBottom: 20, maxWidth: 340 }}>
        <label className="form-label">Departamento</label>
        <select
          className="form-control"
          value={selectedDept}
          onChange={(e) => setSelectedDept(e.target.value)}
        >
          <option value="">Selecione um departamento…</option>
          {departments.map((d) => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
        </select>
      </div>

      {error   && <div className="alert-custom danger"  style={{ marginBottom: 12 }}>{error}</div>}
      {success && <div className="alert-custom success" style={{ marginBottom: 12 }}>{success}</div>}

      {loading && <div style={{ color: 'var(--text-3)', fontSize: 13, padding: '12px 0' }}>Carregando indicadores…</div>}

      {selectedDept && !loading && (
        <>
          {/* Barra de peso total */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 12, color: 'var(--text-3)' }}>
              <span>Peso total dos indicadores ativos</span>
              <span style={{ fontWeight: 700, color: totalWeight > 100 ? 'var(--danger)' : 'var(--success)' }}>
                {totalWeight} pts
              </span>
            </div>
            <div style={{ height: 6, borderRadius: 4, background: 'var(--surface-3)', overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: `${Math.min(totalWeight, 100)}%`,
                background: totalWeight > 100 ? 'var(--danger)' : totalWeight >= 80 ? 'var(--accent)' : 'var(--accent)',
                transition: 'width 0.3s, background 0.3s',
                borderRadius: 4,
              }} />
            </div>
          </div>

          {/* Tabela de slots */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--surface-2)', borderBottom: '2px solid var(--border)' }}>
                  <th style={th}>Slot</th>
                  <th style={th}>Nome do Indicador</th>
                  <th style={{ ...th, textAlign: 'center' }}>Peso (pts)</th>
                  <th style={{ ...th, textAlign: 'center' }}>Ativo</th>
                  <th style={{ ...th, textAlign: 'center' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {SLOTS.map((slot) => {
                  const cfg        = configs.find((c) => c.slot === slot) || null;
                  const isEditing  = editingSlot === slot;

                  return (
                    <tr key={slot} style={{
                      borderBottom: '1px solid var(--border)',
                      background: isEditing ? 'var(--accent-soft)' : 'transparent',
                    }}>
                      <td style={{ ...td, fontWeight: 700, color: 'var(--text-3)', width: 60 }}>#{slot}</td>

                      {isEditing ? (
                        <>
                          <td style={td}>
                            <input
                              className="form-control"
                              style={{ padding: '5px 8px', fontSize: 13 }}
                              value={form.name}
                              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                              placeholder="Nome do indicador"
                              autoFocus
                              onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                            />
                          </td>
                          <td style={{ ...td, textAlign: 'center' }}>
                            <input
                              type="number"
                              className="form-control"
                              style={{ padding: '5px 8px', fontSize: 13, width: 80, textAlign: 'center' }}
                              value={form.weight}
                              min={0}
                              onChange={(e) => setForm((f) => ({ ...f, weight: parseInt(e.target.value) || 0 }))}
                            />
                          </td>
                          <td style={{ ...td, textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={form.active}
                              onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                              style={{ accentColor: 'var(--accent)', width: 16, height: 16 }}
                            />
                          </td>
                          <td style={{ ...td, textAlign: 'center' }}>
                            <button onClick={handleSave} disabled={saving} title="Salvar" style={iconBtn('var(--success)')}>
                              <LuCheck size={16} />
                            </button>
                            <button onClick={() => setEditingSlot(null)} title="Cancelar" style={iconBtn('var(--danger)')}>
                              <LuX size={16} />
                            </button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td style={{ ...td, color: cfg ? 'var(--text-1)' : 'var(--text-3)', fontStyle: cfg ? 'normal' : 'italic' }}>
                            {cfg ? cfg.name : '— não configurado —'}
                          </td>
                          <td style={{ ...td, textAlign: 'center', color: cfg ? 'var(--text-1)' : 'var(--text-3)' }}>
                            {cfg ? `${cfg.weight} pts` : '—'}
                          </td>
                          <td style={{ ...td, textAlign: 'center' }}>
                            {cfg ? (
                              <span style={{
                                display: 'inline-block', padding: '2px 10px', borderRadius: 20,
                                fontSize: 11, fontWeight: 600,
                                background: cfg.active ? 'var(--success-soft)' : 'var(--surface-2)',
                                color:      cfg.active ? 'var(--success)' : 'var(--text-3)',
                              }}>
                                {cfg.active ? 'Ativo' : 'Inativo'}
                              </span>
                            ) : '—'}
                          </td>
                          <td style={{ ...td, textAlign: 'center' }}>
                            <button
                              onClick={() => startEdit(slot, cfg)}
                              title={cfg ? 'Editar' : 'Configurar'}
                              style={iconBtn('var(--text-1)')}
                            >
                              {cfg ? <LuPencil size={14} /> : <LuPlus size={16} />}
                            </button>
                            {cfg && (
                              <button onClick={() => handleRemove(slot)} title="Remover" style={iconBtn('var(--danger)')}>
                                <LuTrash2 size={14} />
                              </button>
                            )}
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!selectedDept && !loading && (
        <div className="empty-state">
          <p>Selecione um departamento para configurar os indicadores</p>
        </div>
      )}
    </div>
  );
}

const th = {
  padding: '10px 14px', textAlign: 'left',
  fontSize: 11, fontWeight: 700, color: 'var(--text-3)',
  textTransform: 'uppercase', letterSpacing: '0.6px',
};

const td = { padding: '10px 14px', color: 'var(--text-2)', verticalAlign: 'middle' };

const iconBtn = (color) => ({
  background: 'none', border: 'none', cursor: 'pointer',
  color, fontSize: 15, padding: '4px 6px', borderRadius: 5,
});
